import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAdminGuard } from '../../hooks/usePermissionGuard';
import { db } from '../../lib/firebase';
import { collection, getDocs, query, where, doc, getDoc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { BookOpen, Search, Filter, ArrowLeft, Eye, Edit, Trash2, Ban, Users, DollarSign, TrendingUp, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';

interface CourseData {
  id: string;
  courseId?: string;
  title: string;
  description: string;
  thumbnail: string;
  price: number;
  instructorId: string;
  instructorName: string;
  instructorEmail: string;
  category: string;
  level: string;
  status: 'active' | 'suspended' | 'draft';
  totalStudents: number;
  totalRevenue: number;
  totalLessons: number;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export default function CoursesManagement() {
  const { user } = useAuthContext();
  const { allowed, loading: guardLoading } = useAdminGuard(true);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<CourseData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended' | 'draft'>('all');
  const [priceFilter, setPriceFilter] = useState<'all' | 'free' | 'paid'>('all');
  const [selectedCourse, setSelectedCourse] = useState<CourseData | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [stats, setStats] = useState({
    totalCourses: 0,
    activeCourses: 0,
    totalStudents: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    if (allowed) {
      loadCourses();
    }
  }, [allowed]);

  useEffect(() => {
    applyFilters();
  }, [courses, searchTerm, statusFilter, priceFilter]);

  async function loadCourses() {
    try {
      setLoading(true);
      const coursesSnap = await getDocs(collection(db, 'courses'));
      const coursesData: CourseData[] = [];

      for (const courseDoc of coursesSnap.docs) {
        const courseData = courseDoc.data();
        
        // Get instructor info
        let instructorName = 'ไม่ระบุ';
        let instructorEmail = '';
        if (courseData.instructorId) {
          const instructorDoc = await getDoc(doc(db, 'users', courseData.instructorId));
          if (instructorDoc.exists()) {
            const instructorData = instructorDoc.data();
            instructorName = instructorData.name || instructorData.displayName || 'ไม่ระบุชื่อ';
            instructorEmail = instructorData.email || '';
          }
        }

        // Get students count from enrollments (actual enrolled students)
        const enrollmentsQuery = query(
          collection(db, 'enrollments'),
          where('courseId', '==', courseDoc.id)
        );
        const enrollmentsSnap = await getDocs(enrollmentsQuery);
        const totalStudents = enrollmentsSnap.size;

        // Get revenue - only count instructor-approved payments
        const paymentsQuery = query(
          collection(db, 'payments'),
          where('courseId', '==', courseDoc.id),
          where('status', '==', 'completed')
        );
        const paymentsSnap = await getDocs(paymentsQuery);
        const totalRevenue = paymentsSnap.docs
          .filter(doc => doc.data().approvedBy)
          .reduce((sum, doc) => {
            return sum + (Number(doc.data().amount) || 0);
          }, 0);

        // Get lessons count
        const lessonsQuery = query(
          collection(db, 'lessons'),
          where('courseId', '==', courseDoc.id)
        );
        const lessonsSnap = await getDocs(lessonsQuery);
        const totalLessons = lessonsSnap.size;

        coursesData.push({
          id: courseDoc.id,
          courseId: courseData.courseId,
          title: courseData.title || 'ไม่มีชื่อ',
          description: courseData.description || '',
          thumbnail: courseData.thumbnail || '',
          price: Number(courseData.price) || 0,
          instructorId: courseData.instructorId || '',
          instructorName,
          instructorEmail,
          category: courseData.category || 'ไม่ระบุ',
          level: courseData.level || 'ไม่ระบุ',
          status: courseData.status || 'active',
          totalStudents,
          totalRevenue,
          totalLessons,
          createdAt: courseData.createdAt?.toDate() || null,
          updatedAt: courseData.updatedAt?.toDate() || null,
        });
      }

      // Sort by created date (newest first)
      coursesData.sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      setCourses(coursesData);

      // Calculate stats
      const totalStudents = coursesData.reduce((sum, c) => sum + c.totalStudents, 0);
      const totalRevenue = coursesData.reduce((sum, c) => sum + c.totalRevenue, 0);
      const activeCourses = coursesData.filter(c => c.status === 'active').length;

      setStats({
        totalCourses: coursesData.length,
        activeCourses,
        totalStudents,
        totalRevenue
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading courses:', error);
      setLoading(false);
    }
  }

  function applyFilters() {
    let filtered = [...courses];

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.instructorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.instructorEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (course.courseId && course.courseId.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(course => course.status === statusFilter);
    }

    // Price filter
    if (priceFilter === 'free') {
      filtered = filtered.filter(course => course.price === 0);
    } else if (priceFilter === 'paid') {
      filtered = filtered.filter(course => course.price > 0);
    }

    setFilteredCourses(filtered);
  }

  const handleSuspendCourse = async (courseId: string, currentStatus: string) => {
    if (!user) return;

    const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    const action = newStatus === 'suspended' ? 'ระงับ' : 'เปิดใช้งาน';
    
    const confirmed = window.confirm(`คุณต้องการ${action}คอร์สนี้ใช่หรือไม่?`);
    if (!confirmed) return;

    setActionLoading(true);
    try {
      const courseRef = doc(db, 'courses', courseId);
      await updateDoc(courseRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      alert(`${action}คอร์สสำเร็จ`);
      await loadCourses();
      setSelectedCourse(null);
    } catch (error: any) {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId: string) => {
    if (!user) return;

    const confirmed = window.confirm(
      'คุณต้องการลบคอร์สนี้ใช่หรือไม่?\n\nการกระทำนี้จะลบ:\n- ข้อมูลคอร์ส\n- บทเรียนทั้งหมด\n- ความคืบหน้าของนักเรียน\n\nแต่จะไม่ลบข้อมูลการชำระเงิน\n\n⚠️ การกระทำนี้ไม่สามารถย้อนกลับได้!'
    );
    if (!confirmed) return;

    const doubleConfirm = window.confirm('ยืนยันอีกครั้ง: คุณแน่ใจหรือไม่ว่าต้องการลบคอร์สนี้?');
    if (!doubleConfirm) return;

    setActionLoading(true);
    try {
      // Delete lessons
      const lessonsQuery = query(collection(db, 'lessons'), where('courseId', '==', courseId));
      const lessonsSnap = await getDocs(lessonsQuery);
      for (const lessonDoc of lessonsSnap.docs) {
        await deleteDoc(lessonDoc.ref);
      }

      // Delete progress
      const progressQuery = query(collection(db, 'progress'), where('courseId', '==', courseId));
      const progressSnap = await getDocs(progressQuery);
      for (const progressDoc of progressSnap.docs) {
        await deleteDoc(progressDoc.ref);
      }

      // Delete course
      await deleteDoc(doc(db, 'courses', courseId));

      alert('ลบคอร์สสำเร็จ');
      await loadCourses();
      setSelectedCourse(null);
    } catch (error: any) {
      alert(`เกิดข้อผิดพลาด: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'ไม่มีข้อมูล';
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  if (guardLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลดข้อมูลคอร์ส...</p>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">ไม่มีสิทธิ์เข้าถึง</h2>
          <p className="text-gray-600">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                <ArrowLeft className="w-6 h-6 text-textDark" />
              </button>
            </Link>
            <BookOpen className="w-10 h-10 text-blue-600" />
            <div>
              <h1 className="text-4xl font-bold text-textDark">จัดการคอร์สทั้งหมด</h1>
              <p className="text-textLight">จัดการและติดตามคอร์สทั้งหมดในระบบ</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">คอร์สทั้งหมด</p>
                <p className="text-3xl font-bold text-gray-800">{stats.totalCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">คอร์สที่เปิดใช้งาน</p>
                <p className="text-3xl font-bold text-gray-800">{stats.activeCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">นักเรียนทั้งหมด</p>
                <p className="text-3xl font-bold text-gray-800">{stats.totalStudents}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">รายได้รวม</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(stats.totalRevenue)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-3xl p-6 shadow-xl mb-8">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textLight" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="ค้นหาคอร์ส, ผู้สอน, หมวดหมู่..."
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-600 focus:outline-none transition-colors"
              />
            </div>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-textLight" />
                <span className="text-sm font-semibold text-textDark">กรองตาม:</span>
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option value="all">สถานะทั้งหมด</option>
                <option value="active">เปิดใช้งาน</option>
                <option value="suspended">ระงับ</option>
                <option value="draft">แบบร่าง</option>
              </select>

              <select
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              >
                <option value="all">ราคาทั้งหมด</option>
                <option value="free">ฟรี</option>
                <option value="paid">เสียเงิน</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              แสดง <span className="font-semibold text-blue-600">{filteredCourses.length}</span> จาก {courses.length} คอร์ส
            </div>
          </div>
        </div>

        {/* Courses List */}
        <div className="bg-white rounded-3xl p-8 shadow-xl">
          {filteredCourses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">ไม่พบคอร์ส</h3>
              <p className="text-gray-600">ลองเปลี่ยนเงื่อนไขการค้นหาหรือกรอง</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {filteredCourses.map((course) => (
                <div
                  key={course.id}
                  className="border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-blue-200 transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Thumbnail */}
                    <div className="md:w-64 h-48 md:h-auto bg-gray-100 flex-shrink-0">
                      {course.thumbnail ? (
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-16 h-16 text-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-800 mb-2">{course.title}</h3>
                          {course.courseId && (
                            <p className="text-sm font-mono text-blue-600 mb-2">รหัส: {course.courseId}</p>
                          )}
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{course.description}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span>ผู้สอน: {course.instructorName}</span>
                            {course.instructorEmail && (
                              <span className="text-gray-400">({course.instructorEmail})</span>
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          {course.status === 'active' ? (
                            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              เปิดใช้งาน
                            </span>
                          ) : course.status === 'suspended' ? (
                            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center gap-1">
                              <Ban className="w-3 h-3" />
                              ระงับ
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              แบบร่าง
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-blue-600 mb-1">ราคา</p>
                          <p className="font-bold text-blue-700">
                            {course.price === 0 ? 'ฟรี' : formatCurrency(course.price)}
                          </p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-blue-600 mb-1">นักเรียน</p>
                          <p className="font-bold text-blue-700">{course.totalStudents} คน</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-xs text-green-600 mb-1">รายได้</p>
                          <p className="font-bold text-green-700">{formatCurrency(course.totalRevenue)}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs text-blue-600 mb-1">บทเรียน</p>
                          <p className="font-bold text-blue-700">{course.totalLessons} บท</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="text-xs text-gray-500">
                          สร้างเมื่อ: {formatDate(course.createdAt)}
                        </div>
                        <div className="flex items-center gap-2">
                          <Link to={`/courses/${course.id}`}>
                            <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition-colors flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              ดู
                            </button>
                          </Link>
                          <button
                            onClick={() => setSelectedCourse(course)}
                            className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition-colors flex items-center gap-1"
                          >
                            <Edit className="w-4 h-4" />
                            จัดการ
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Course Management Modal */}
        {selectedCourse && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">จัดการคอร์ส</h3>
                <button
                  onClick={() => setSelectedCourse(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {/* Course Info */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 mb-6">
                <div className="flex gap-4 mb-4">
                  {selectedCourse.thumbnail ? (
                    <img
                      src={selectedCourse.thumbnail}
                      alt={selectedCourse.title}
                      className="w-24 h-24 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-blue-200 rounded-xl flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-blue-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-gray-800 mb-1">{selectedCourse.title}</h4>
                    {selectedCourse.courseId && (
                      <p className="text-xs font-mono text-blue-600 mb-1">รหัส: {selectedCourse.courseId}</p>
                    )}
                    <p className="text-sm text-gray-600 mb-2">โดย {selectedCourse.instructorName}</p>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        selectedCourse.status === 'active' ? 'bg-green-100 text-green-700' :
                        selectedCourse.status === 'suspended' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {selectedCourse.status === 'active' ? 'เปิดใช้งาน' :
                         selectedCourse.status === 'suspended' ? 'ระงับ' : 'แบบร่าง'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {selectedCourse.price === 0 ? 'ฟรี' : formatCurrency(selectedCourse.price)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">นักเรียน</p>
                    <p className="font-semibold text-blue-700">{selectedCourse.totalStudents} คน</p>
                  </div>
                  <div className="bg-white rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">รายได้</p>
                    <p className="font-semibold text-green-700">{formatCurrency(selectedCourse.totalRevenue)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">บทเรียน</p>
                    <p className="font-semibold text-blue-700">{selectedCourse.totalLessons} บท</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-4">
                {/* Edit Course */}
                <div className="border-2 border-blue-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Edit className="w-5 h-5 text-blue-600" />
                    <h5 className="font-bold text-gray-800">แก้ไขคอร์ส</h5>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    เข้าสู่หน้าแก้ไขคอร์สในฐานะแอดมิน
                  </p>
                  <Link to={`/instructor/courses/${selectedCourse.id}/edit?admin=true`}>
                    <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
                      <Edit className="w-5 h-5" />
                      แก้ไขคอร์ส
                    </button>
                  </Link>
                </div>

                {/* View Course */}
                <div className="border-2 border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-5 h-5 text-blue-600" />
                    <h5 className="font-bold text-gray-800">ดูคอร์ส</h5>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    ดูหน้าคอร์สแบบที่นักเรียนเห็น
                  </p>
                  <Link to={`/courses/${selectedCourse.id}`}>
                    <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2">
                      <Eye className="w-5 h-5" />
                      เปิดดูคอร์ส
                    </button>
                  </Link>
                </div>

                {/* Suspend/Activate */}
                <div className="border-2 border-gray-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Ban className="w-5 h-5 text-blue-600" />
                      <h5 className="font-bold text-gray-800">สถานะคอร์ส</h5>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    {selectedCourse.status === 'suspended'
                      ? 'คอร์สนี้ถูกระงับอยู่ นักเรียนไม่สามารถเข้าถึงได้'
                      : 'ระงับคอร์สจะทำให้นักเรียนไม่สามารถเข้าถึงได้ชั่วคราว'}
                  </p>
                  <button
                    onClick={() => handleSuspendCourse(selectedCourse.id, selectedCourse.status)}
                    disabled={actionLoading}
                    className={`w-full py-3 rounded-xl font-semibold transition-all disabled:opacity-50 ${
                      selectedCourse.status === 'suspended'
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {actionLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        กำลังดำเนินการ...
                      </span>
                    ) : selectedCourse.status === 'suspended' ? (
                      <span className="flex items-center justify-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        เปิดใช้งานคอร์ส
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Ban className="w-5 h-5" />
                        ระงับคอร์ส
                      </span>
                    )}
                  </button>
                </div>

                {/* Delete Course */}
                <div className="border-2 border-red-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Trash2 className="w-5 h-5 text-red-600" />
                    <h5 className="font-bold text-gray-800">ลบคอร์ส</h5>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    ลบคอร์สและข้อมูลทั้งหมด (บทเรียน, ความคืบหน้า) แต่ไม่ลบข้อมูลการชำระเงิน
                    <br />
                    <span className="text-red-600 font-semibold">⚠️ การกระทำนี้ไม่สามารถย้อนกลับได้!</span>
                  </p>
                  <button
                    onClick={() => handleDeleteCourse(selectedCourse.id)}
                    disabled={actionLoading}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        กำลังลบ...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-5 h-5" />
                        ลบคอร์สถาวร
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Close Button */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setSelectedCourse(null)}
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-xl font-semibold transition-all"
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
